import React, { useCallback, useEffect, useRef, useState } from "react";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { Paginator } from "primereact/paginator";
import { ProgressSpinner } from "primereact/progressspinner";
import { toast } from "react-toastify";
import {
  listMedia,
  listFolders,
  uploadMedia,
  formatBytes,
  isImage,
  isVideo,
} from "../../api/mediaApi";
import "./media.scss";

/**
 * Browsing half of the media library: filters, upload and the asset grid.
 *
 * Shared by the full Media Library page and the picker dialog so both stay in
 * step — one place decides how assets are searched, uploaded and rendered.
 *
 * Props:
 *   selectedId     — currently highlighted asset id
 *   onSelect       — (item) => void, fired on single click
 *   onPick         — (item) => void, fired on double click (picker shortcut)
 *   typeLock       — force a type filter ("image") and hide the type dropdown
 *   refreshKey     — bump to force a reload from the parent
 *   onChanged      — called after uploads so the parent can refresh too
 */
const TYPE_OPTIONS = [
  { label: "All types", value: "all" },
  { label: "Images", value: "image" },
  { label: "Videos", value: "video" },
  { label: "Documents", value: "document" },
];

const VISIBILITY_FILTERS = [
  { label: "Any visibility", value: "all" },
  { label: "Public — on the website", value: "public" },
  { label: "Whole school", value: "school" },
  { label: "Private — only me", value: "private" },
];

const MediaBrowser = ({
  selectedId,
  onSelect,
  onPick,
  typeLock = null,
  refreshKey = 0,
  onChanged,
  pageSize = 24,
}) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [first, setFirst] = useState(0);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [folder, setFolder] = useState("all");
  const [type, setType] = useState(typeLock || "all");
  const [visibility, setVisibility] = useState("all");
  const [folders, setFolders] = useState([]);

  const fileRef = useRef(null);

  // Keep typing responsive — one request per pause, not per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listMedia({
        q: debounced || undefined,
        folder,
        type: typeLock || (type === "all" ? undefined : type),
        visibility: visibility === "all" ? undefined : visibility,
        page: Math.floor(first / pageSize) + 1,
        limit: pageSize,
      });
      setItems(res.items || []);
      setTotal(res.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not load media");
    } finally {
      setLoading(false);
    }
  }, [debounced, folder, type, visibility, typeLock, first, pageSize]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const loadFolders = useCallback(async () => {
    try {
      const res = await listFolders();
      setFolders(res.folders || []);
    } catch {
      /* the filter just stays on "All folders" */
    }
  }, []);

  useEffect(() => {
    loadFolders();
  }, [loadFolders, refreshKey]);

  // Filters change the result set — go back to the first page or the user
  // lands on an empty page.
  useEffect(() => {
    setFirst(0);
  }, [debounced, folder, type, visibility]);

  const handleFiles = async (files) => {
    const picked = Array.from(files || []);
    if (!picked.length) return;
    setUploading(true);
    try {
      const res = await uploadMedia(picked, {
        folder: folder === "all" ? "General" : folder,
      });
      // No toast: the grid reloads and selects the new file, which is the
      // feedback. Only failures interrupt.
      setFirst(0);
      await load();
      await loadFolders();
      onChanged?.();
      // Newly uploaded file is almost always the one you wanted
      if (res.items[0]) onSelect?.(res.items[0]);
    } catch (err) {
      toast.error(err.response?.data?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer?.files);
  };

  const folderOptions = [
    { label: "All folders", value: "all" },
    ...folders.map((f) => ({ label: `${f.name} (${f.count})`, value: f.name })),
  ];

  const renderThumb = (item) => {
    if (isImage(item)) {
      return <img src={item.file_path} alt={item.alt_text || item.title || ""} loading="lazy" />;
    }
    return (
      <div className="media-thumb-icon">
        <i className={isVideo(item) ? "pi pi-video" : "pi pi-file"} />
      </div>
    );
  };

  return (
    <div className="media-browser">
      <div className="media-toolbar">
        <span className="p-input-icon-left flex-1">
          <i className="pi pi-search" />
          <InputText
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, title, alt text or tag"
            className="w-full"
          />
        </span>
        <Dropdown
          value={folder}
          options={folderOptions}
          onChange={(e) => setFolder(e.value)}
          className="media-filter"
        />
        {!typeLock && (
          <Dropdown
            value={type}
            options={TYPE_OPTIONS}
            onChange={(e) => setType(e.value)}
            className="media-filter"
          />
        )}
        <Dropdown
          value={visibility}
          options={VISIBILITY_FILTERS}
          onChange={(e) => setVisibility(e.value)}
          className="media-filter"
        />
        <Button
          label="Upload"
          icon="pi pi-upload"
          loading={uploading}
          onClick={() => fileRef.current?.click()}
        />
        <input
          ref={fileRef}
          type="file"
          multiple
          accept={typeLock === "image" ? "image/*" : undefined}
          className="hidden"
          style={{ display: "none" }}
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = "";
          }}
        />
      </div>

      <div
        className={`media-grid-wrap${dragOver ? " drag-over" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
      >
        {loading ? (
          <div className="media-empty">
            <ProgressSpinner style={{ width: 40, height: 40 }} strokeWidth="4" />
          </div>
        ) : items.length === 0 ? (
          <div className="media-empty">
            <i className="pi pi-images" />
            <p className="m-0">Nothing here yet</p>
            <small>Drop files anywhere in this area, or use the Upload button.</small>
          </div>
        ) : (
          <div className="media-grid">
            {items.map((item) => (
              <button
                type="button"
                key={item.id}
                className={`media-card${item.id === selectedId ? " selected" : ""}`}
                onClick={() => onSelect?.(item)}
                onDoubleClick={() => onPick?.(item)}
                title={item.original_name}
              >
                <div className="media-thumb">{renderThumb(item)}</div>
                <div className="media-meta">
                  <span className="media-name">{item.title || item.original_name}</span>
                  <span className="media-sub">
                    {formatBytes(item.file_size)}
                    {item.width ? ` · ${item.width}×${item.height}` : ""}
                  </span>
                </div>
                {item.visibility !== "school" && (
                  <span
                    className="media-badge"
                    title={
                      item.visibility === "public"
                        ? "Public — anyone can view this on your website"
                        : "Private — only you and your admins can see this"
                    }
                  >
                    <i className={item.visibility === "public" ? "pi pi-globe" : "pi pi-lock"} />
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
        {dragOver && <div className="media-drop-hint">Drop files to upload</div>}
      </div>

      {total > pageSize && (
        <Paginator
          first={first}
          rows={pageSize}
          totalRecords={total}
          onPageChange={(e) => setFirst(e.first)}
        />
      )}
    </div>
  );
};

export default MediaBrowser;
